import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, finalize } from 'rxjs';

import { TitleBarComponent } from '../shared/components/title-bar/title-bar.component';
import { SearchbarComponent } from '../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../shared/components/action-button/action-button.component';
import { LinkNavComponent } from '../shared/components/link-nav/link-nav.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../shared/components/pagination-bar/pagination-bar.component';
import { LoadingTableShimmerComponent } from '../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { EmptyResultComponent } from '../shared/components/empty-result/empty-result.component';
import {
  FilterModalComponent,
  FilterCategory,
  FilterModalAmountRangeConfig,
  FilterModalCustomRangeConfig,
  FilterModalAmountRange,
} from '../shared/components/filter-modal/filter-modal.component';
import {
  DateRangePickerComponent,
  DateRange,
  PeriodOption,
} from '../shared/components/date-range-picker/date-range-picker.component';
import { ConfirmActionModalComponent } from '../shared/components/confirm-action-modal/confirm-action-modal.component';
import {
  formatCalendarDateInTimeZone,
  formatDateToISO,
  getUserTimezone,
} from '../shared/utils/date.util';
import {
  EXPORT_CUSTOM_PERIOD,
  EXPORT_PERIOD_OPTIONS,
  resolveExportDateRange,
} from '../shared/utils/export-date-presets.util';
import { notifyDownloadError } from '../shared/utils/download-error.util';

import { ListingTableComponent } from '../shared/components/listing-table';
import {
  DataTableFilter,
  DataTableFilterChange,
  DataTableSortState,
} from '../shared/components/data-table';
import { CustomDiscountTableComponent } from './components/custom-discount-table/custom-discount-table.component';
import { CreateDiscountModalComponent } from './components/create-discount-modal/create-discount-modal.component';
import { DiscountDetailsModalComponent } from './components/discount-details-modal/discount-details-modal.component';

import { LocationService } from '../services/location.service';
import { ToastService } from '../services/toast.service';
import { LoggerService } from '../services/logger.service';
import { EmployeeService } from '../services/employee.service';
import { DiscountService } from './services/discount.service';
import {
  CustomDiscountRow,
  Discount,
  DiscountListPagination,
  DiscountTabId,
} from './models/discount.models';
import {
  CUSTOM_SHIMMER_COLUMNS,
  DEFAULT_DISCOUNT_PAGE_SIZE,
  DISCOUNT_ITEMS_PER_PAGE_OPTIONS,
  DISCOUNT_STATUS_LIST_FILTER_OPTIONS,
  DISCOUNT_TABS,
  DISCOUNT_TYPE_LIST_FILTER_OPTIONS,
  SUMMARY_SHIMMER_COLUMNS,
} from './constants/discount.constants';
import {
  buildCustomDiscountListApiFilters,
  buildDiscountFilterCategories,
  buildDiscountSummaryListApiFilters,
  countActiveDiscountFilters,
  createDefaultDiscountAppliedFilters,
  DiscountAppliedFilters,
  resolveCustomDiscountFiltersFromCategories,
  resolveSummaryFiltersFromCategories,
  toDataTableFilterOptions,
} from './utils/discount-list-filter.util';

/**
 * Discount screen (CWS-1176): Discounts Summary + Custom Discount tabs.
 *
 * Summary rows come from svc-package (`package/discounts`); Custom Discount rows come
 * from svc-order (`order/custom-discounts`), which owns the order/customer joins.
 */
@Component({
  selector: 'app-discount',
  standalone: true,
  imports: [
    CommonModule,
    TitleBarComponent,
    SearchbarComponent,
    ActionButtonComponent,
    LinkNavComponent,
    PaginationBarComponent,
    LoadingTableShimmerComponent,
    EmptyResultComponent,
    FilterModalComponent,
    DateRangePickerComponent,
    ConfirmActionModalComponent,
    ListingTableComponent,
    CustomDiscountTableComponent,
    CreateDiscountModalComponent,
    DiscountDetailsModalComponent,
  ],
  templateUrl: './discount.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./discount.component.scss'],
})
export class DiscountComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly discountService = inject(DiscountService);
  private readonly locationService = inject(LocationService);
  private readonly employeeService = inject(EmployeeService);
  private readonly toastService = inject(ToastService);
  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly navLinks = DISCOUNT_TABS;
  readonly itemsPerPageOptions = DISCOUNT_ITEMS_PER_PAGE_OPTIONS;
  readonly summaryShimmerColumns = SUMMARY_SHIMMER_COLUMNS;
  readonly customShimmerColumns = CUSTOM_SHIMMER_COLUMNS;
  readonly summaryColumnFilterMap = { type: 'discountType', status: 'status' };

  activeTab: DiscountTabId = 'summary';

  // Toolbar state — shared Location + Date/Period for both tabs (payments pattern)
  searchTerm = '';
  /** Empty string = All Locations. */
  /** Location options for the Filter modal's multi-select (was the toolbar dropdown). */
  filterLocationOptions: { id: string; label: string }[] = [];
  /** Defaults to Today (payments pattern). */

  // Rows
  summaryRows: Discount[] = [];
  customRows: CustomDiscountRow[] = [];
  loading = false;

  // Sorting — column keys match Discount API `sort_by` fields
  sort: DataTableSortState = { column: 'created_at', direction: 'desc' };

  // Pagination
  currentPage = 1;
  itemsPerPage = DEFAULT_DISCOUNT_PAGE_SIZE;
  totalItems = 0;

  // Filters
  showFilterModal = false;
  appliedFilters: DiscountAppliedFilters = createDefaultDiscountAppliedFilters();
  /** Stable filter-modal state — rebuilt only on open/close/tab/clear, not every change detection. */
  readonly filterCategories = signal<FilterCategory[]>([]);
  filterModalAmountRange: FilterModalAmountRange = { minAmount: null, maxAmount: null };
  /** Created Date draft, mirrored from appliedFilters when the modal opens. */
  filterModalDateRange: DateRange = { startDate: null, endDate: null };
  /** Cached options for Custom Discount Attendant / Customer multi-selects. */
  private filterAttendants: { id: string; label: string }[] = [];
  /** Customers from the custom-discounts list (same source as Customer Name). */
  private filterAttendantsLoaded = false;

  // Create / edit / view
  showDiscountModal = false;
  discountBeingEdited: Discount | null = null;
  discountBeingViewed: Discount | null = null;

  // Delete
  discountPendingDelete: Discount | null = null;
  deleting = false;

  // CSV export — same presets/custom range flow as Orders → Download filtered orders as CSV
  isCsvDateRangePickerOpen = false;
  isExporting = false;
  csvDateRange: DateRange = { startDate: null, endDate: null };
  csvSelectedPeriod = EXPORT_CUSTOM_PERIOD;
  readonly csvExportPeriodOptions: PeriodOption[] = EXPORT_PERIOD_OPTIONS;

  ngOnInit(): void {
    this.syncFilterCategoriesFromApplied();
    this.initLocationsAndRows();
    this.loadCustomAttendantFilterOptions();
    this.openDiscountFromQueryParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Orders page links here with `?discountId=` — open the existing details modal
   * for that discount (same path as the Summary table View action).
   */
  private openDiscountFromQueryParams(): void {
    const discountId = String(
      this.route.snapshot.queryParamMap.get('discountId') ??
        new URLSearchParams(window.location.search).get('discountId') ??
        ''
    ).trim();
    if (!discountId) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });

    this.discountService
      .getDiscountById(discountId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const discount = response?.data;
          if (!discount?.id) {
            this.toastService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Discount not found.',
            });
            return;
          }
          this.activeTab = 'summary';
          this.discountBeingViewed = discount;
        },
        error: error => {
          this.logger.error('Failed to open discount from query param', error);
          this.toastService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error?.message || 'Failed to load discount details.',
          });
        },
      });
  }

  // ── Toolbar ────────────────────────────────────────────────────────────────

  /**
   * One-shot fetch (same as the Orders page). Subscribing to locations$ instead would
   * emit the BehaviorSubject's initial empty value and load the table twice.
   */
  /**
   * Locations and rows are fetched concurrently.
   *
   * Waiting for location/view-location-user before starting the discount request made
   * the page cost both round trips in series, even though the discount API answers
   * quickly. The header already warms the location cache, so the options are usually
   * available synchronously here; when they are not, the list still starts immediately
   * (tenant-scoped) and is re-fetched only if the resolved default location changes the
   * query.
   */
  private initLocationsAndRows(): void {
    this.applyLocationOptions();
    this.loadRows();

    this.locationService
      .getLocations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.applyLocationOptions();
        },
        error: error => {
          // Rows are already loading; the location filter just stays unset.
          this.logger.error('Failed to load locations for discounts', error);
        },
      });
  }

  private applyLocationOptions(): void {
    this.filterLocationOptions = this.locationService
      .getLocationOptions()
      .map(option => ({ id: String(option.value ?? '').trim(), label: option.label }))
      .filter(option => option.id.length > 0);
    this.syncFilterCategoriesFromApplied();
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.currentPage = 1;
    this.loadRows();
  }

  onTabChange(tabId: string): void {
    if (tabId === this.activeTab) {
      return;
    }
    this.activeTab = tabId as DiscountTabId;
    this.currentPage = 1;
    this.summaryRows = [];
    this.customRows = [];
    this.totalItems = 0;
    this.syncFilterCategoriesFromApplied();
    this.loadRows();
  }

  onTableSortChange(sort: DataTableSortState): void {
    this.sort = sort;
    this.currentPage = 1;
    this.loadRows();
  }

  toggleCreatedDateSort(): void {
    this.onTableSortChange({
      column: 'created_at',
      direction: this.sort.direction === 'desc' ? 'asc' : 'desc',
    });
  }

  // ── Filters ────────────────────────────────────────────────────────────────

  get isSummaryTab(): boolean {
    return this.activeTab === 'summary';
  }

  getActiveFilterCount(): number {
    return countActiveDiscountFilters(this.isSummaryTab, this.appliedFilters);
  }

  get hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  get hasSearchOrFilters(): boolean {
    if (this.searchTerm.trim().length > 0 || this.hasActiveFilters) {
      return true;
    }
    // Location and the date range both moved into the Filter modal, so hasActiveFilters above
    // already accounts for them.
    return false;
  }

  /** Created Date pickers in the Filter modal, replacing the removed toolbar period dropdown. */
  get filterModalCustomRangeConfig(): FilterModalCustomRangeConfig {
    return {
      categoryId: 'createdDate',
      startLabel: 'Start Date',
      endLabel: 'End Date',
      startDate: this.filterModalDateRange.startDate,
      endDate: this.filterModalDateRange.endDate,
      maxDate: this.todayInShopTimeZone(),
    };
  }

  /**
   * Latest selectable day for the Custom Date range: a discount cannot have been created in the
   * future, so future days are not offerable.
   *
   * Resolved in the SHOP timezone, not the browser's — the list and the created-date filter both
   * bucket by the shop zone, so a user in IST must not be offered a day that has not started in
   * Los Angeles yet (it would always return nothing).
   */
  private todayInShopTimeZone(): Date {
    const isoDay = formatCalendarDateInTimeZone(new Date(), getUserTimezone());
    const [year, month, day] = isoDay.split('-').map(Number);
    // Built as a local-midnight Date so it compares cleanly against the picker's own values.
    return new Date(year, month - 1, day);
  }

  get filterModalAmountRangeConfig(): FilterModalAmountRangeConfig | null {
    if (this.isSummaryTab) {
      return null;
    }
    return {
      categoryId: 'amountRange',
      minLabel: 'Min Amount',
      maxLabel: 'Max Amount',
      minAmount: this.filterModalAmountRange.minAmount,
      maxAmount: this.filterModalAmountRange.maxAmount,
    };
  }

  /** Column-header filters for the Discounts Summary table (Type + Status). */
  get summaryTableFilters(): DataTableFilter[] {
    const filters: DataTableFilter[] = [
      {
        field: 'discountType',
        label: 'Type',
        type: 'checkbox',
        options: toDataTableFilterOptions(DISCOUNT_TYPE_LIST_FILTER_OPTIONS),
        activeValues: [...this.appliedFilters.discountType],
      },
    ];

    if (this.isSummaryTab) {
      filters.push({
        field: 'status',
        label: 'Status',
        type: 'checkbox',
        options: toDataTableFilterOptions(DISCOUNT_STATUS_LIST_FILTER_OPTIONS),
        activeValues: [...this.appliedFilters.status],
      });
    }

    return filters;
  }

  onTableFilterChange(change: DataTableFilterChange): void {
    if (change.field === 'status') {
      this.appliedFilters = { ...this.appliedFilters, status: [...change.values] };
    } else if (change.field === 'discountType') {
      this.appliedFilters = { ...this.appliedFilters, discountType: [...change.values] };
    } else {
      return;
    }
    this.currentPage = 1;
    this.loadRows();
  }

  onFilter(): void {
    this.syncFilterModalDraftFromApplied();
    this.syncFilterCategoriesFromApplied();
    this.showFilterModal = true;
  }

  onCloseFilterModal(): void {
    this.showFilterModal = false;
    this.syncFilterModalDraftFromApplied();
    this.syncFilterCategoriesFromApplied();
  }

  onAmountRangeChange(range: FilterModalAmountRange): void {
    this.filterModalAmountRange = {
      minAmount: range.minAmount,
      maxAmount: range.maxAmount,
    };
  }

  onCustomRangeChange(range: DateRange): void {
    this.filterModalDateRange = {
      startDate: range.startDate ? new Date(range.startDate) : null,
      endDate: range.endDate ? new Date(range.endDate) : null,
    };
  }

  onFilterOptionChange(event: {
    categoryId: string;
    optionId: string;
    categories: FilterCategory[];
  }): void {
    this.filterCategories.set(event.categories);
  }

  onApplyFilters(categories: FilterCategory[]): void {
    if (this.isSummaryTab) {
      this.appliedFilters = resolveSummaryFiltersFromCategories(categories, this.appliedFilters);
    } else {
      this.appliedFilters = resolveCustomDiscountFiltersFromCategories(
        categories,
        this.appliedFilters,
        this.filterModalAmountRange.minAmount,
        this.filterModalAmountRange.maxAmount
      );
    }
    this.appliedFilters = {
      ...this.appliedFilters,
      ...this.resolveCreatedDateFilters(),
    };
    this.showFilterModal = false;
    this.currentPage = 1;
    this.loadRows();
  }

  /**
   * A Created Date range only filters once BOTH ends are set — a half-open range would other-
   * wise silently drop rows. `dateRange` stays null until then so the API omits the dates.
   */
  private resolveCreatedDateFilters(): Pick<
    DiscountAppliedFilters,
    'dateRange' | 'customStartDate' | 'customEndDate'
  > {
    const { startDate, endDate } = this.filterModalDateRange;
    if (!startDate || !endDate) {
      return { dateRange: null, customStartDate: null, customEndDate: null };
    }
    return { dateRange: 'custom', customStartDate: startDate, customEndDate: endDate };
  }

  private syncFilterModalDraftFromApplied(): void {
    this.filterModalAmountRange = {
      minAmount: this.appliedFilters.minAmount,
      maxAmount: this.appliedFilters.maxAmount,
    };
    this.filterModalDateRange = {
      startDate: this.appliedFilters.customStartDate,
      endDate: this.appliedFilters.customEndDate,
    };
  }

  /** Rebuild filter modal categories from appliedFilters (discards in-progress modal edits). */
  private syncFilterCategoriesFromApplied(): void {
    this.filterCategories.set(
      buildDiscountFilterCategories(this.isSummaryTab, this.appliedFilters, {
        locations: this.filterLocationOptions,
        attendants: this.filterAttendants,
      })
    );
  }

  /**
   * Load Attendant options for the Custom Discount filter modal.
   * Customer options come from the custom-discounts list response (same join as
   * the Customer Name column) — see `applyCustomDiscountCustomerFilterOptions`.
   */
  private loadCustomAttendantFilterOptions(): void {
    if (this.filterAttendantsLoaded) {
      return;
    }
    this.filterAttendantsLoaded = true;

    this.employeeService
      .loadEmployees(1, 1000, '', { status: 'active' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          const employees = Array.isArray(result?.employees) ? result.employees : [];
          this.filterAttendants = employees
            .map(
              (employee: {
                user_id?: string;
                id?: string;
                first_name?: string;
                last_name?: string;
                name?: string;
              }) => {
                const id = String(employee.user_id ?? '').trim();
                if (!id) {
                  return null;
                }
                const label =
                  String(employee.name ?? '').trim() ||
                  `${String(employee.first_name ?? '').trim()} ${String(employee.last_name ?? '').trim()}`.trim() ||
                  id;
                return { id, label };
              }
            )
            .filter(
              (row: { id: string; label: string } | null): row is { id: string; label: string } =>
                row !== null
            );
          this.syncFilterCategoriesFromApplied();
        },
        error: error => {
          this.logger.error('Failed to load attendants for discount filters', error);
        },
      });
  }

  // ── Data ───────────────────────────────────────────────────────────────────

  /** Typed as PaginationData so the app-pagination-bar contract is compiler-checked. */
  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.itemsPerPage > 0 ? Math.ceil(this.totalItems / this.itemsPerPage) : 0,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  get hasRows(): boolean {
    return this.isSummaryTab ? this.summaryRows.length > 0 : this.customRows.length > 0;
  }

  loadRows(): void {
    if (this.isSummaryTab) {
      this.loadSummaryRows();
      return;
    }
    this.loadCustomRows();
  }

  private applyPagination(pagination: DiscountListPagination): void {
    this.totalItems = pagination.total;
    // A deleted last row can leave the viewer past the end; step back and refetch.
    const lastPage = Math.max(1, pagination.total_pages);
    if (this.currentPage > lastPage) {
      this.currentPage = lastPage;
      this.loadRows();
    }
  }

  private loadSummaryRows(): void {
    this.loading = true;
    this.discountService
      .getDiscounts({
        search: this.searchTerm.trim() || undefined,
        type: 'normal',
        ...buildDiscountSummaryListApiFilters(this.appliedFilters),
        sort_by: this.sort.column,
        order_by: this.sort.direction,
        page: this.currentPage,
        limit: this.itemsPerPage,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.summaryRows = result.rows;
          this.applyPagination(result.pagination);
          this.loading = false;
        },
        error: error => {
          this.logger.error('Failed to load discounts', error);
          this.summaryRows = [];
          this.totalItems = 0;
          this.loading = false;
          this.toastService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load discounts. Please try again.',
          });
        },
      });
  }

  private loadCustomRows(): void {
    this.loading = true;
    const apiFilters = buildCustomDiscountListApiFilters(this.appliedFilters);
    this.discountService
      .getCustomDiscounts({
        search: this.searchTerm.trim() || undefined,
        ...apiFilters,
        sort_by: 'created_at',
        order_by: this.sort.direction,
        page: this.currentPage,
        limit: this.itemsPerPage,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.customRows = result.rows;
          this.applyPagination(result.pagination);
          this.loading = false;
        },
        error: error => {
          this.logger.error('Failed to load custom discounts', error);
          this.customRows = [];
          this.totalItems = 0;
          this.loading = false;
          this.toastService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load custom discounts. Please try again.',
          });
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadRows();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;
    this.currentPage = 1;
    this.loadRows();
  }

  // ── Create / edit / delete ─────────────────────────────────────────────────

  onViewDiscount(discount: Discount): void {
    this.discountBeingViewed = discount;
  }

  onCloseDetails(): void {
    this.discountBeingViewed = null;
  }

  onEditFromDetails(discount: Discount): void {
    this.discountBeingViewed = null;
    this.onEditDiscount(discount);
  }

  onCreateDiscount(): void {
    this.discountBeingEdited = null;
    this.showDiscountModal = true;
  }

  onEditDiscount(discount: Discount): void {
    this.discountBeingEdited = discount;
    this.showDiscountModal = true;
  }

  onCloseDiscountModal(): void {
    this.showDiscountModal = false;
    this.discountBeingEdited = null;
  }

  /**
   * Editing returns a NEW discount id (the API versions rows), so always refetch
   * instead of patching the row in place.
   */
  onDiscountSaved(): void {
    this.showDiscountModal = false;
    this.discountBeingEdited = null;
    this.activeTab = 'summary';
    this.loadRows();
  }

  onDeleteDiscount(discount: Discount): void {
    this.discountPendingDelete = discount;
  }

  onCancelDelete(): void {
    this.discountPendingDelete = null;
  }

  get deleteMessage(): string {
    const name = this.discountPendingDelete?.name || 'this discount';
    return `“${name}” will no longer be available to apply. Orders that already used it keep their discount.`;
  }

  onConfirmDelete(): void {
    const discount = this.discountPendingDelete;
    if (!discount || this.deleting) {
      return;
    }

    this.deleting = true;
    this.discountService
      .deleteDiscount(discount.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deleting = false;
          this.discountPendingDelete = null;
          this.toastService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: 'Discount deleted successfully.',
          });
          this.loadRows();
        },
        error: error => {
          this.logger.error('Failed to delete discount', error);
          this.deleting = false;
          this.toastService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error?.message || 'Failed to delete discount. Please try again.',
          });
        },
      });
  }

  // ── Export ─────────────────────────────────────────────────────────────────

  /** Opens the Orders page with the order details modal for this row. */
  onViewOrder(row: CustomDiscountRow): void {
    this.router.navigate(['/orders'], { queryParams: { orderId: row.order_uuid } });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.csv-export-wrapper') && this.isCsvDateRangePickerOpen) {
      this.closeCsvDateRangePicker();
    }
  }

  /** Same entry point as Orders → Download filtered orders as CSV. */
  toggleCsvDateRangePicker(): void {
    if (this.isExporting) {
      return;
    }
    this.isCsvDateRangePickerOpen = !this.isCsvDateRangePickerOpen;
  }

  closeCsvDateRangePicker(): void {
    this.isCsvDateRangePickerOpen = false;
  }

  onCsvDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    const range = resolveExportDateRange(data.period, data.customRange, getUserTimezone());
    if (!range) {
      this.toastService.add({
        severity: 'warn',
        summary: 'Date Range Required',
        detail: 'Please select both a start date and an end date.',
      });
      return;
    }

    this.csvSelectedPeriod = data.period;
    this.csvDateRange = range;
    this.closeCsvDateRangePicker();
    this.exportFilteredToCsv();
  }

  private exportFilteredToCsv(): void {
    const { startDate, endDate } = this.csvDateRange;
    if (!startDate || !endDate || this.isExporting) {
      return;
    }

    if (this.isSummaryTab) {
      this.exportSummaryCsv(formatDateToISO(startDate), formatDateToISO(endDate));
      return;
    }
    this.exportCustomCsv(formatDateToISO(startDate), formatDateToISO(endDate));
  }

  private exportSummaryCsv(start: string, end: string): void {
    this.isExporting = true;
    const apiFilters = buildDiscountSummaryListApiFilters(this.appliedFilters);
    this.discountService
      .downloadDiscountsCsv({
        ...(apiFilters.location_id !== undefined ? { location_id: apiFilters.location_id } : {}),
        search: this.searchTerm.trim() || undefined,
        type: 'normal',
        discount_type: apiFilters.discount_type,
        is_active: apiFilters.is_active,
        // Export date range overrides the toolbar period (Orders CSV pattern).
        created_start_date: start,
        created_end_date: end,
        sort_by: this.sort.column,
        order_by: this.sort.direction,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isExporting = false;
        })
      )
      .subscribe({
        next: blob =>
          this.handleExportBlob(blob, `discounts_summary_${start}_${end}.csv`, 'Discounts Summary'),
        error: error => this.handleExportError('Failed to export discounts summary', error),
      });
  }

  private exportCustomCsv(start: string, end: string): void {
    const locationIds = this.resolveExportLocationIds();
    if (locationIds.length === 0) {
      this.toastService.add({
        severity: 'warn',
        summary: 'No locations',
        detail: 'Add a location before exporting custom discounts.',
      });
      return;
    }

    this.isExporting = true;
    const apiFilters = buildCustomDiscountListApiFilters(this.appliedFilters);
    this.discountService
      .downloadCustomDiscountsCsv({
        search: this.searchTerm.trim() || undefined,
        discount_type: apiFilters.discount_type,
        applied_by: apiFilters.applied_by,
        min_amount: apiFilters.min_amount,
        max_amount: apiFilters.max_amount,
        // Export date range overrides the toolbar period (Orders CSV pattern).
        start_date: start,
        end_date: end,
        location_id: locationIds.length === 1 ? locationIds[0] : locationIds,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isExporting = false;
        })
      )
      .subscribe({
        next: blob =>
          this.handleExportBlob(blob, `custom_discounts_${start}_${end}.csv`, 'Custom Discounts'),
        error: error => this.handleExportError('Failed to export custom discounts', error),
      });
  }

  /**
   * Custom Discount CSV API requires location_id. The Filter modal selection is passed as-is;
   * an empty selection ("all locations") expands to every known location id for the tenant.
   */
  private resolveExportLocationIds(): string[] {
    if (this.appliedFilters.locations.length > 0) {
      return this.appliedFilters.locations;
    }
    return this.filterLocationOptions.map(option => option.id).filter(Boolean);
  }

  private handleExportBlob(blob: Blob, filename: string, label: string): void {
    if (!blob?.size) {
      this.toastService.add({
        severity: 'info',
        summary: 'No Data Found',
        detail: `There are no ${label.toLowerCase()} matching the selected date range and filters.`,
      });
      return;
    }

    this.downloadBlob(blob, filename);
    this.toastService.add({
      severity: 'success',
      summary: 'CSV Downloaded',
      detail: `${label} CSV downloaded successfully.`,
    });
  }

  private handleExportError(logMessage: string, error: unknown): void {
    this.logger.error(logMessage, error);
    notifyDownloadError(error, 'Unable to download the CSV. Please try again.', detail =>
      this.toastService.add({ severity: 'error', summary: 'Export Failed', detail })
    );
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
